import { useEffect, useRef, useState } from 'react'
import type { MeDto } from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Brandmark } from '../components/Brandmark.js'
import { useText } from '../i18n.js'
import { useNavigate } from '../router.js'
import { rememberTalkStart } from './visitIntent.js'

/**
 * Die Zwischenstation zwischen Landingpage und Demo-Board.
 *
 * Sie besteht nur, damit der Knopf auf der statischen Seite ein schlichter Link
 * sein kann: Die Seite dort trägt kein Skript — die Sicherheitsrichtlinie
 * verbietet Inline-Code — und könnte deshalb selbst kein POST absetzen.
 *
 * `asTalk` unterscheidet die beiden Türen, die auf dasselbe Board führen:
 * „selbst vortragen" beginnt sofort im Vortragsmodus auf Frame 1, „selbst
 * bauen" auf der offenen Leinwand. Es ist dieselbe Demo — nur der erste
 * Eindruck unterscheidet sich, und der entscheidet, was jemand als Erstes
 * begreift.
 */
export function DemoPage({
  onSignedIn,
  asTalk = false,
}: {
  onSignedIn: (user: MeDto) => void
  asTalk?: boolean
}): React.ReactElement {
  const t = useText()
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  /*
   * React ruft Effekte im Strict-Modus doppelt auf. Ohne diese Sperre entstünden
   * bei jedem Aufruf zwei Demo-Organisationen — die zweite unbenutzt, aber eine
   * Stunde lang da.
   */
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    api
      .startDemo()
      .then((demo) => {
        onSignedIn(demo.me)
        if (asTalk) rememberTalkStart(demo.boardId)
        navigate(`/b/${demo.boardId}`, true)
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : t('demo.failed')),
      )
  }, [asTalk, navigate, onSignedIn, t])

  return (
    <div className="auth-screen brand-surface">
      <div className="auth-card">
        <Brandmark />
        {error ? (
          <>
            <p className="form-error">{error}</p>
            <a className="button ghost" href="/de/">
              {t('common.back')}
            </a>
          </>
        ) : (
          <p className="auth-lead">{t(asTalk ? 'demo.preparingTalk' : 'demo.preparing')}</p>
        )}
      </div>
    </div>
  )
}
